// Pure logic for the Apple delegated-onboarding console: which environment to display, whether a
// step's queue button may be pressed and WHY NOT, and the exact value objects the one write helper
// sends to Firestore.
//
// Deliberately free of React and of `@firecms/*` imports, so the two things that matter most in this
// build — the write boundary and the gating — can be read and audited in isolation. Everything that
// touches the CMS runtime lives in `apple_delegated_onboarding.write.ts` and the components.
//
// ⚠️ THE REFUSAL COPY IS NOT ORIGINAL. Every message keyed on a specific state is lifted from the
// backend's own `onboarding-readiness.ts` (`brandRefusalMessage`, `mediaRefusalMessage`,
// `locationRefusalMessage`) so a staff member reads the SAME sentence whether the console refused or
// the drain did. Rewriting one of these in nicer words is how the two start to disagree.

import {
  APPLE_DELEGATED_ONBOARDING_PATH,
  APPLE_MEDIA_STATE_LABELS,
  APPLE_RESOURCE_STATE_LABELS,
  AppleDelegatedOnboardingEntity,
  AppleEnvironmentEnum,
  AppleEnvScoped,
  AppleMediaStateEnum,
  AppleResourceStateEnum,
  DelegatedBrandContractStateEnum,
  EnvScopedBrandState,
  EnvScopedMediaState,
  NON_REQUEUEABLE_FAILURE_CODES,
  ONBOARDING_GALLERY_PHOTO_LABEL,
  ONBOARDING_GALLERY_PHOTOS_PATH,
  ONBOARDING_MAX_ATTEMPTS,
  ONBOARDING_SHOWCASE_LABEL,
  ONBOARDING_SHOWCASE_REQUESTS_PATH,
  ONBOARDING_STEP_FORM_OWNED_FIELDS,
  ONBOARDING_STEP_LABELS,
  ONBOARDING_STEP_NAMES,
  ONBOARDING_STEP_NON_FORM_FIELDS,
  ONBOARDING_STEP_RESOURCES,
  OnboardingBrandAssetsInput,
  OnboardingBrandInput,
  OnboardingFailureCodeEnum,
  OnboardingGalleryPhotoInput,
  OnboardingLocationInput,
  OnboardingMediaInput,
  OnboardingShowcaseInput,
  OnboardingStepName,
  OnboardingStepStatus,
  OnboardingSubmitStateEnum,
  REQUEUEABLE_FAILURE_CODES,
  SUBMIT_STATE_LABELS,
} from "./apple_delegated_onboarding.types";

/* ------------------------------------------------------------------------------------------------
 * Environment resolution — the env-scoping trap, handled once
 * ---------------------------------------------------------------------------------------------- */

/**
 * ⚠️ `submitState` IS FLAT AND ENV-AGNOSTIC WHILE RESULTS ARE ENV-SCOPED. A step run in DQE reads
 * `SUBMITTED` in every environment, because there is one intent and three result buckets. So
 * "submitted" must never be rendered as "done in the environment you are looking at": the only
 * honest answer comes from `apple.<ENV>`, and the environment has to be NAMED on screen.
 *
 * This resolves WHICH bucket to display from the document itself rather than from a build-time
 * constant, for two reasons. There is no env var in this CMS that could tell it (and the prompt for
 * this slice is explicit that none should be added), and the deployed backend currently writes
 * `DQE` — derived from the Business Connect host in `constants.ts:54` — which is a value this app
 * cannot observe and which will change without a CMS deploy when the host flips to production.
 *
 * Picks the most recently written bucket, so a document that has only ever been touched by one
 * environment resolves to that one, and a document later re-run in production follows.
 */
export const resolveEnvBucket = <T>(
  apple: AppleEnvScoped<T> | null | undefined,
): { env: AppleEnvironmentEnum; bucket: T } | null => {
  if (!apple) return null;
  const candidates: { env: AppleEnvironmentEnum; bucket: T; at: number }[] = [];
  for (const env of [
    AppleEnvironmentEnum.PRODUCTION,
    AppleEnvironmentEnum.DQE,
    AppleEnvironmentEnum.AIE,
  ]) {
    const bucket = apple[env];
    if (bucket === undefined || bucket === null) continue;
    candidates.push({ env, bucket, at: toMillis(readUnknownDate(bucket, "observedAt")) });
  }
  if (candidates.length === 0) return null;
  // Most recent `observedAt` wins; a bucket with no `observedAt` sorts last but is still eligible,
  // so an incomplete write never makes the whole step unreadable.
  candidates.sort((a, b) => b.at - a.at);
  return { env: candidates[0].env, bucket: candidates[0].bucket };
};

/**
 * The environment this DOCUMENT is being worked in, resolved across all four steps.
 *
 * Used for the cross-step gates (a location needs its brand's bucket; a showcase needs the brand's
 * AND the location's), which must all read the SAME environment — a DQE brand can never satisfy a
 * production location, and mixing buckets is how that check would silently pass.
 */
export const resolveDocumentEnvironment = (
  values: Partial<AppleDelegatedOnboardingEntity> | null | undefined,
): AppleEnvironmentEnum | null => {
  if (!values) return null;
  let best: { env: AppleEnvironmentEnum; at: number } | null = null;
  for (const step of ONBOARDING_STEP_NAMES) {
    // Explicitly `unknown`: the four steps have differently-shaped buckets, and only `observedAt` —
    // read by name — matters for picking the environment.
    const resolved = resolveEnvBucket<unknown>(values[step]?.apple);
    if (!resolved) continue;
    const at = toMillis(readUnknownDate(resolved.bucket, "observedAt"));
    if (!best || at > best.at) best = { env: resolved.env, at };
  }
  return best?.env ?? null;
};

/**
 * Whether an env bucket's `state` / `contractState` are LIVE (refreshed by the drain's mirror phase)
 * rather than a create-time seed.
 *
 * ⚠️ READ BY NAME OFF `unknown`, AND THAT DESIGN HAS NOW PAID OFF. When this console was written the
 * `location` bucket had no `mirroredAt` at all, so `location.apple.<ENV>.state` was a permanently
 * stale create-time seed and any gate keyed on it would have blocked forever. The backend has since
 * added `location` to `ONBOARDING_MIRRORED_STEP_NAMES` and given its bucket a `mirroredAt`
 * (`apple-delegated-onboarding.schema.ts:765`), and because this reads the field structurally rather
 * than through a bucket type, `evaluateLocationCtaGate`'s publication branch began enforcing Apple's
 * real precondition with no change here.
 *
 * The check still means the same thing everywhere: `mirroredAt` ABSENT ⇒ `state` / `contractState`
 * are create-time seeds, not live. `locationCta` is the one bucket that will never have it — its
 * step writes its own result from a synchronous Apple response, so there is nothing to mirror.
 */
export const isLiveMirroredEvidence = (bucket: unknown): boolean =>
  readUnknownDate(bucket, "mirroredAt") !== null;

/**
 * Reads a possibly-undeclared date field off a backend-written bucket.
 *
 * Takes `unknown` rather than a bucket type on purpose — see `isLiveMirroredEvidence`. Reading
 * structurally is what let the location bucket's `mirroredAt` start being honoured the day the
 * backend added it, with no change on this side.
 */
const readUnknownDate = (bucket: unknown, key: string): Date | null => {
  if (typeof bucket !== "object" || bucket === null) return null;
  const raw = (bucket as Record<string, unknown>)[key];
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw === "string" || typeof raw === "number") {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const toMillis = (date: Date | null): number => (date ? date.getTime() : 0);

/** When the mirror last refreshed this bucket, or `null` if it never has. */
export const mirroredAtOf = (bucket: unknown): Date | null => readUnknownDate(bucket, "mirroredAt");

/* ------------------------------------------------------------------------------------------------
 * Failure handling — built to survive an enum that keeps growing
 * ---------------------------------------------------------------------------------------------- */

/**
 * Whether staff may re-queue after this failure.
 *
 * ⚠️ THE FAILURE-CODE ENUM IS NOT STABLE — it keeps growing, and this CMS ships
 * a hand-copied list. So an UNRECOGNISED code is never treated as an error and never as "fine": it
 * falls back to `submitState`, which the backend does keep meaningful. `REJECTED` means the backend
 * proved nothing reached Apple; `NEEDS_ATTENTION` means it could not. A new code therefore lands
 * with correct behaviour on the day it ships, without a CMS deploy.
 */
export const isRequeueable = (
  failureCode: string | null | undefined,
  submitState: OnboardingSubmitStateEnum | undefined,
): boolean => {
  if (failureCode && NON_REQUEUEABLE_FAILURE_CODES.includes(failureCode)) return false;
  if (failureCode && REQUEUEABLE_FAILURE_CODES.includes(failureCode)) return true;
  // Unrecognised (or absent) code — defer to the state the backend still owns. Safe in both
  // directions: the backend's invariant is `needsAttention === !requeueable`, so a code that is
  // genuinely unsafe always arrives as `NEEDS_ATTENTION`, which `evaluateCommonGate` blocks before
  // any failure code is consulted.
  return submitState === OnboardingSubmitStateEnum.REJECTED;
};

/** Whether this console's shipped copy of the enum knows the code at all. */
export const isKnownFailureCode = (failureCode: string | null | undefined): boolean =>
  Boolean(failureCode) && KNOWN_FAILURE_CODES.has(failureCode as string);

// The full enum, from the same single source the two ownership lists are derived from — so "known"
// cannot drift away from "re-queueable" the way a second hand-maintained copy did.
const KNOWN_FAILURE_CODES = new Set<string>(Object.values(OnboardingFailureCodeEnum));

/**
 * The one-line note shown ABOVE the verbatim `failureMessage` when this console does not recognise
 * the code — never instead of it.
 *
 * A code we have never heard of must render as "newer than this screen", not as a blank space and
 * not as a crash. The message underneath is the backend's own sentence and is always the real
 * answer, which is why this line says so explicitly.
 */
export const describeUnknownFailureCode = (
  failureCode: string,
  submitState: OnboardingSubmitStateEnum | undefined,
): string =>
  `Failure code "${failureCode}" is newer than this screen, so it is shown as the backend wrote it. ` +
  `Re-queueing is treated as ${
    submitState === OnboardingSubmitStateEnum.REJECTED ? "SAFE" : "UNSAFE"
  } based on the step's status (${
    SUBMIT_STATE_LABELS[submitState ?? OnboardingSubmitStateEnum.DRAFT] ?? submitState
  }). The message below is the authoritative explanation.`;

/* ------------------------------------------------------------------------------------------------
 * Status rendering
 * ---------------------------------------------------------------------------------------------- */

/** The single status a staff member reads for a step, in plain language. */
export const friendlyStepStatus = (status: OnboardingStepStatus | null | undefined): string => {
  const state = status?.submitState;
  if (!state) return SUBMIT_STATE_LABELS[OnboardingSubmitStateEnum.DRAFT];
  return SUBMIT_STATE_LABELS[state] ?? `Unknown (${state})`;
};

/**
 * Chip colours for the backend-computed brand-asset pair state. Keys are FireCMS `ChipColorKey`s.
 *
 * ⚠️ `INCOMPLETE` IS RED, NOT AMBER, and that is the one colour judgement worth arguing. It means
 * exactly one asset exists — which sounds like partial progress and is not: Apple renders the branding
 * as a SET, so the place card shows NO branding at all. It is a live client-visible defect, and the
 * least useful thing this console could do is make it look like a step in progress.
 *
 * An unrecognised value falls through to grey at the call site, so a state newer than this build
 * degrades to a neutral chip rather than an absent one.
 */
export const BRAND_ASSET_PAIR_STATE_COLORS: Record<string, string> = {
  NOT_STARTED: "grayLight",
  UPLOADING: "blueLight",
  IN_REVIEW: "blueLight",
  ACTION_NEEDED: "orangeDark",
  INCOMPLETE: "redDark",
  LIVE: "greenDark",
};

/** Chip colours keyed by the raw intent state. Keys are FireCMS `ChipColorKey` values. */
export const SUBMIT_STATE_COLORS: Record<string, string> = {
  [OnboardingSubmitStateEnum.DRAFT]: "grayLight",
  [OnboardingSubmitStateEnum.QUEUED]: "blueLight",
  [OnboardingSubmitStateEnum.PROCESSING]: "blueLight",
  [OnboardingSubmitStateEnum.SUBMITTED]: "greenLight",
  [OnboardingSubmitStateEnum.REJECTED]: "orangeDark",
  [OnboardingSubmitStateEnum.NEEDS_ATTENTION]: "redDark",
};

/**
 * The staleness sentence for a mirrored step, or `null` when the mirror is current.
 *
 * Surfaced rather than swallowed: a create-time seed presented as live truth is how someone concludes
 * "Apple still has not published this" when in fact nothing has looked since the day it was created.
 */
export const describeMirrorFreshness = (bucket: unknown, now: Date): string | null => {
  if (typeof bucket !== "object" || bucket === null) return null;
  const mirroredAt = mirroredAtOf(bucket);
  if (!mirroredAt) {
    return (
      "Apple's state below is the value recorded when this step ran — the status mirror has not " +
      "refreshed it yet, so treat it as create-time evidence rather than live truth."
    );
  }
  const hours = (now.getTime() - mirroredAt.getTime()) / 3_600_000;
  if (hours < MIRROR_STALE_AFTER_HOURS) return null;
  return (
    `Apple's state below was last refreshed ${Math.floor(hours)} hours ago. The mirror runs on the ` +
    `same 5-minute drain as everything else, so a gap this large means it has stopped — ask ` +
    `engineering before trusting the state below.`
  );
};

/**
 * How old a mirrored value may get before this console calls it stale. Generous on purpose: the
 * mirror rides the 5-minute drain, so anything under a few hours is ordinary lag or a quiet
 * document, and crying wolf here would train staff to ignore the warning that matters.
 */
const MIRROR_STALE_AFTER_HOURS = 6;

/* ------------------------------------------------------------------------------------------------
 * THE GATE
 * ---------------------------------------------------------------------------------------------- */

/**
 * Which surface is being queued. The five embedded steps plus the two SUBCOLLECTIONS — showcase
 * requests and gallery photos, neither of which is an embedded step.
 *
 * ⚠️ DERIVED FROM `ONBOARDING_STEP_RESOURCES`, NOT SPELLED OUT AGAIN, and that is the whole point of
 * this line. Every gated surface in this console routes through `evaluateQueueGate`, and every
 * message it produces needs a resource noun; making the union `keyof typeof` that table means a new
 * target cannot exist without a noun, and a noun cannot be added without `evaluatePerStepGate`'s
 * switch failing to compile until it has a branch. (The backend does exactly this with
 * `OnboardingResourceStep`.) The previous hand-written union was the second place the answer lived.
 */
export type QueueTarget = keyof typeof ONBOARDING_STEP_RESOURCES;

/**
 * The FIELD PATH a target's status fields live at: an embedded step's key, or `null` for a
 * subcollection document whose status sits at its ROOT.
 *
 * ⚠️ ONE DEFINITION OF THIS MAPPING, because it was previously inlined as
 * `target === "showcase" ? null : target` in the steps view and hardcoded as `step: null` in the
 * showcase entity action — and adding a second root-status surface (gallery photos) would have made
 * that ternary silently WRONG rather than merely duplicated: `"galleryPhoto"` is not a step key, so
 * a merge write would have created a bogus `galleryPhoto: { submitState }` map on the photo document
 * and the drain would never have seen the queue.
 *
 * `null` is not "unknown" — it is "this document's status fields are at its root", which is what
 * `wrapStepValues` and `buildQueueIntentValues` mean by it.
 */
export const stepFieldOf = (target: QueueTarget): OnboardingStepName | null =>
  target === "showcase" || target === "galleryPhoto" ? null : target;

/**
 * The result of gating one queue button.
 *
 * `cautions` on an ALLOWED gate are the things this console genuinely cannot decide — they are shown
 * next to the enabled button rather than used to disable it, because a button that cannot be pressed
 * and cannot be explained is what produces hand-edited Firestore documents.
 */
export type QueueGate = { allowed: true; cautions: string[] } | { allowed: false; reason: string };

const block = (reason: string): QueueGate => ({ allowed: false, reason });
const allow = (...cautions: (string | null)[]): QueueGate => ({
  allowed: true,
  cautions: cautions.filter((c): c is string => Boolean(c)),
});

/** What a blocked/allowed message calls the thing this step writes. */
const resourceOf = (target: QueueTarget) => ONBOARDING_STEP_RESOURCES[target];

/**
 * What staff CALL the thing being queued — one heading, one refusal, one snackbar, one word.
 *
 * ⚠️ EXPORTED, because the components and the write helper were each deriving it themselves and the
 * write helper's version was keyed on `step === null` — which with two root-status subcollections
 * would have reported a queued GALLERY PHOTO as "Showcase request queued". A label a staff member
 * reads after pressing a button has to be the same label that was on the button.
 */
export const queueTargetLabel = (target: QueueTarget): string => {
  if (target === "showcase") return ONBOARDING_SHOWCASE_LABEL;
  if (target === "galleryPhoto") return ONBOARDING_GALLERY_PHOTO_LABEL;
  return ONBOARDING_STEP_LABELS[target];
};

/**
 * THE CLIENT-SIDE READINESS GATE — design §0 #6, "every button is gated with a stated reason".
 *
 * ⚠️ IT IS A COURTESY TO STAFF, NOT A GUARD ON THE DATA, and the comments here say so because the
 * backend's own gate says so: this is a browser client whose Firestore rule grants write to any
 * authenticated principal, so the drain re-parses and re-checks everything below server-side. What
 * this buys is that a staff member learns WHY a step is refused at the moment they try, instead of
 * five minutes later via a failure code — and that a needless attempt is not spent.
 *
 * ⚠️ IT DOES NOT REPLICATE THE SERVER'S AUTHORITATIVE READS. The server checks
 * `apple_delegated_brands` / `_media` / `_locations`; this reads only the mirror on the one document
 * the CMS opens. Where the mirror cannot answer, the gate ALLOWS with a stated caution rather than
 * inventing a verdict — see `evaluateLocationCtaGate`.
 */
export const evaluateQueueGate = (args: {
  target: QueueTarget;
  /** The step map, or for a showcase the request document itself (status lives at its root). */
  step: (OnboardingStepStatus & { input?: unknown; companyId?: string | null }) | null | undefined;
  /**
   * The onboarding document. For a showcase this is the PARENT, fetched separately;
   * `null` means it could not be read, which becomes a caution rather than a refusal.
   */
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  /** The environment the cross-step gates read. `null` when the document has no Apple result yet. */
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { target, step, parent, env } = args;
  const common = evaluateCommonGate(target, step, ctaDeferredOn(target, parent, env));
  if (common) return common;

  // Cautions that apply to every target, merged into whatever the per-step gate decides. Kept
  // separate from `evaluateCommonGate` (which only ever BLOCKS) so the block ordering there stays
  // readable, and so a caution can never be mistaken for a refusal.
  const shared = [...commonCautions(target, step)];

  // The counterpart to `ctaDeferredOn`'s unblocking: staff need to know what a second press DOES here,
  // because the step reads SUBMITTED and normally that means "finished".
  if (ctaDeferredOn(target, parent, env)) {
    shared.push(
      `The “About the place” text and “Good to Know” amenities for this practice are already on Apple; ` +
        `only the booking button is missing, because Apple had not offered it yet. Queueing again ` +
        `re-checks Apple and sets the button if it is available now. It re-sends the same content, ` +
        `which changes nothing, and it cannot create a duplicate — this step only updates a location ` +
        `that already exists. If Apple still does not offer it, you will get this same message back.`,
    );
  }

  // The counterpart to the SUBMITTED unblock for a recurring target: the chip reads Submitted, which
  // everywhere else in this console means "done", so the button has to say what pressing it again does.
  if (isRecurring(target) && step?.submitState === OnboardingSubmitStateEnum.SUBMITTED) {
    shared.push(
      `This practice's holiday hours and payment methods are already on Apple. Queueing again sends ` +
        `whatever the location step's form holds NOW, replacing what was sent last time — which is how ` +
        `you add next year's holidays or correct an entry. It cannot create a duplicate of anything: ` +
        `this step only updates a location that already exists. Holiday entries the client added ` +
        `themselves in Business Connect are left alone, but the payment-method list is REPLACED, so ` +
        `tick everything the practice accepts and not only what changed.`,
    );
  }

  const gate = evaluatePerStepGate({ target, step, parent, env });
  if (!gate.allowed || shared.length === 0) return gate;
  return { allowed: true, cautions: [...shared, ...gate.cautions] };
};

/**
 * The hazard restated on the press that actually triggers the Apple write.
 *
 * ⚠️ RE-OPEN IS NOT THE DANGEROUS CLICK — QUEUE IS. Re-open moves a parked step to `DRAFT` and
 * deliberately RETAINS `failureCode` / `failureMessage`, which is what keeps the evidence. But
 * `evaluateCommonGate`'s non-requeueable check only fires on `REJECTED`, so a re-opened `DRAFT` step
 * still carrying, say, `SHOWCASE_CREATIVE_ORPHANED` would otherwise be queued with nothing said at
 * all — relying on a separate notice block elsewhere on the screen to carry the warning. The second
 * press is the one that can mint a second live Apple resource; it should be the one that says so.
 */
const commonCautions = (
  target: QueueTarget,
  step: OnboardingStepStatus | null | undefined,
): string[] => {
  const state = step?.submitState ?? OnboardingSubmitStateEnum.DRAFT;
  const code = step?.failureCode;
  if (state !== OnboardingSubmitStateEnum.DRAFT || !code) return [];

  // ⚠️ POSITIVE MEMBERSHIP, NOT `!isRequeueable(...)`, AND THE DIFFERENCE IS THE WHOLE POINT. This
  // sentence asserts that a real Apple resource may exist. `isRequeueable` also returns false for an
  // UNRECOGNISED code, so keying on it made this console FABRICATE a duplicate-hazard warning for any
  // code newer than the build — which is exactly what `GRANT_ACCOUNT_MISMATCH` did: a refusal the
  // backend raises before it even decrypts a token, announced to staff as "a brand may already
  // exist". An unknown code gets the conservative block plus the "newer than this screen" line from
  // `describeUnknownFailureCode`; it must never get this.
  if (!NON_REQUEUEABLE_FAILURE_CODES.includes(code)) return [];

  const { noun, mintsResource } = resourceOf(target);
  return [
    mintsResource
      ? `⚠️ This step was re-opened after a failure ("${code}") whose Apple outcome could NOT be ` +
        `proven harmless — a ${noun} may already exist under this client's Apple company. Queueing ` +
        `now can create a SECOND one, and Apple gives us no way to undo it. Only press Queue if you ` +
        `have checked Apple Business Connect since. The full reason is on this step above.`
      : `⚠️ This step was re-opened after a failure ("${code}") that kept failing. Nothing can be ` +
        `duplicated — it only updates a location that already exists — but if the cause has not ` +
        `changed it will simply fail again. The full reason is on this step above.`,
  ];
};

/** The per-target prerequisites, once the common blocks have passed. */
const evaluatePerStepGate = (args: {
  target: QueueTarget;
  step: (OnboardingStepStatus & { input?: unknown; companyId?: string | null }) | null | undefined;
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { target, step, parent, env } = args;
  switch (target) {
    case "brand":
      return evaluateBrandGate(step?.input as OnboardingBrandInput | null | undefined);
    case "brandAssets":
      return evaluateBrandAssetsGate(step?.input as OnboardingBrandAssetsInput | null | undefined);
    case "media":
      return evaluateMediaGate(step?.input as OnboardingMediaInput | null | undefined);
    case "location":
      return evaluateLocationGate({
        input: step?.input as OnboardingLocationInput | null | undefined,
        pinnedCompanyId: step?.companyId,
        brandBucket: env ? parent?.brand?.apple?.[env] : undefined,
        env,
      });
    case "locationCta":
      return evaluateLocationCtaGate({ parent: parent ?? null, env });
    case "locationDetails":
      return evaluateLocationDetailsGate({
        // ⚠️ READS THE LOCATION STEP'S INPUT, NOT ITS OWN — see `OnboardingLocationDetailsStep`.
        parent: parent ?? null,
        pinnedCompanyId: step?.companyId,
        env,
      });
    case "showcase":
      return evaluateShowcaseGate({
        input: step?.input as OnboardingShowcaseInput | null | undefined,
        parent,
        env,
      });
    case "galleryPhoto":
      return evaluateGalleryPhotoGate({
        input: step?.input as OnboardingGalleryPhotoInput | null | undefined,
        pinnedCompanyId: step?.companyId,
        parent: parent ?? null,
        env,
      });
  }
};

/**
 * The blocks that apply to every step, in the order they must be read.
 *
 * `NEEDS_ATTENTION` is checked before the attempts cap deliberately: the cap sets that state, and
 * when both apply the ambiguous-outcome warning is the one a staff member has to act on.
 */
/**
 * Whether this step is a SUBMITTED place-card step whose booking button was NOT set because Apple did
 * not offer it — the one case where re-queueing an already-submitted step is both safe and necessary.
 *
 * ⚠️ WHY THIS EXISTS. The backend now writes the place-card CONTENT even when Apple will not take the
 * booking CTA, because the CTA only becomes available once Apple approves the scheduling link and
 * refusing meanwhile held a clinic's description hostage to that approval. The step therefore lands
 * SUBMITTED with the content written and the button still unset — and the plain SUBMITTED block would
 * then tell staff there is "nothing to re-run" and to ask engineering, which is exactly wrong: the
 * thing that has to happen is a re-queue once Apple offers the CTA.
 *
 * ⚠️ AND IT IS ONLY SAFE BECAUSE THIS STEP MINTS NOTHING (`mintsResource: false`). Re-running it is an
 * UPDATE of a location that already exists, so there is no duplicate to create — the same reason the
 * SUBMITTED message for this step already says nothing can have been duplicated. Do not generalise
 * this to a minting step.
 */
/**
 * Targets a staff member is EXPECTED to queue again after a successful run.
 *
 * ⚠️ EXACTLY ONE TODAY, AND IT IS A LIST RATHER THAN A COMPARISON SO THE REASON IS WRITABLE. Every
 * other target is worked once: a create cannot be re-run without duplicating, and the booking-button
 * step is set and done (its one legitimate re-run is `ctaDeferredOn`, a different and narrower
 * condition — Apple did not offer the CTA *yet*). `locationDetails` is the first target whose whole
 * purpose is repetition: a practice has holidays every December and changes what it accepts at the
 * desk, and its write is a converging full-replace update, so the second run is as safe as the first.
 *
 * ⚠️ ADDING A TARGET HERE UNBLOCKS QUEUEING AN ALREADY-SUBMITTED STEP. That is safe ONLY for a target
 * whose Apple write mints nothing — check `ONBOARDING_STEP_RESOURCES[target].mintsResource` before you
 * do it. On a create it would hand staff a one-click duplicate with no undo.
 */
const RECURRING_TARGETS: readonly QueueTarget[] = ["locationDetails"];

const isRecurring = (target: QueueTarget): boolean => RECURRING_TARGETS.includes(target);

const ctaDeferredOn = (
  target: QueueTarget,
  parent: Partial<AppleDelegatedOnboardingEntity> | null,
  env: AppleEnvironmentEnum | null,
): boolean => {
  if (target !== "locationCta" || !env) return false;
  const message = parent?.locationCta?.apple?.[env]?.ctaNotOfferedMessage;
  return typeof message === "string" && message.trim().length > 0;
};

const evaluateCommonGate = (
  target: QueueTarget,
  step: OnboardingStepStatus | null | undefined,
  /** See `ctaDeferredOn` — the one reason a SUBMITTED step may legitimately be queued again. */
  ctaDeferred = false,
): QueueGate | null => {
  const state = step?.submitState ?? OnboardingSubmitStateEnum.DRAFT;
  const attempts = step?.attempts ?? 0;
  const { noun, mintsResource } = resourceOf(target);
  const label = queueTargetLabel(target);

  if (!KNOWN_SUBMIT_STATES.has(state)) {
    return block(
      `This step's status reads "${state}", which this console does not recognise, so it will not ` +
        `queue anything — the value may have been hand-edited. Ask engineering to look at the ` +
        `document before pressing anything.`,
    );
  }

  if (state === OnboardingSubmitStateEnum.QUEUED) {
    return block(
      `${label} is already queued. The drain runs every 5 minutes and claims it transactionally, so ` +
        `pressing again does nothing except confuse the audit trail — wait for the status to change.`,
    );
  }

  if (state === OnboardingSubmitStateEnum.PROCESSING) {
    return block(
      `${label} is being processed right now: the drain has claimed it and is calling Apple. ` +
        `Nothing to do — the status changes on its own when Apple answers.`,
    );
  }

  // ⚠️ A RECURRING TARGET IS NEVER BLOCKED FOR BEING SUBMITTED. For it, `SUBMITTED` means "the last
  // run landed", not "finished" — see `RECURRING_TARGETS`. `evaluateQueueGate` adds the caution that
  // says what a second press does, because the status chip still reads Submitted and that normally
  // means there is nothing to do.
  if (state === OnboardingSubmitStateEnum.SUBMITTED && !ctaDeferred && !isRecurring(target)) {
    return block(
      mintsResource
        ? `${label} has already been submitted to Apple, so a ${noun} exists for this practice. ` +
            `Queueing it again would create a SECOND one — Apple's creates take no idempotency key, ` +
            `so there is no way to undo that from here. If the recorded result looks wrong, ask ` +
            `engineering rather than re-queueing.`
        : `${label} has already been applied to Apple. There is nothing to re-run unless something ` +
            `about the location changed; ask engineering if you believe the button is not set.`,
    );
  }

  if (state === OnboardingSubmitStateEnum.NEEDS_ATTENTION) {
    return block(
      mintsResource
        ? `Apple's outcome for ${label.toLowerCase()} is UNKNOWN — a previous attempt may have ` +
            `reached Apple without us seeing the answer. Re-queueing could mint a SECOND live ${noun} ` +
            `under this client's Apple company, and Apple gives us no way to undo it. Read the ` +
            `failure message below, check Apple Business Connect for this client's ${noun}s, and only ` +
            `then use "Re-open …" to put the step back to Draft.`
        : `${label} failed repeatedly and has been parked. Nothing can have been duplicated — this ` +
            `step only UPDATES a location that already exists — but something is failing every time. ` +
            `Read the failure message below, then use "Re-open …" to put the step back to Draft if ` +
            `you have addressed it.`,
    );
  }

  if (attempts >= ONBOARDING_MAX_ATTEMPTS) {
    return block(
      mintsResource
        ? `This step has used all ${ONBOARDING_MAX_ATTEMPTS} attempts and each one ended WITHOUT a ` +
            `confirmed result. Failures that prove nothing reached Apple reset that counter, so ` +
            `reaching the cap means a previous attempt may have reached Apple. Check Apple Business ` +
            `Connect for this client's ${noun}s before anyone re-queues this.`
        : `This step has used all ${ONBOARDING_MAX_ATTEMPTS} attempts. Nothing can have been ` +
            `duplicated — it only updates a location that already exists — so something is failing ` +
            `repeatedly. Read the last failure message below and ask engineering.`,
    );
  }

  // A terminal failure whose code proves an Apple resource may exist. Reachable only from a
  // hand-edited document today (the backend pairs those codes with `NEEDS_ATTENTION`), which is
  // exactly why it is checked rather than assumed away.
  if (
    state === OnboardingSubmitStateEnum.REJECTED &&
    step?.failureCode &&
    !isRequeueable(step.failureCode, state)
  ) {
    return block(
      `The last failure ("${step.failureCode}") is one that cannot prove nothing was created at ` +
        `Apple, so this console will not re-queue it. Read the failure message below and check Apple ` +
        `Business Connect first.`,
    );
  }

  return null;
};

const KNOWN_SUBMIT_STATES = new Set<string>(Object.values(OnboardingSubmitStateEnum));

/* ---------------------------------- per-step prerequisites ------------------------------------ */

/**
 * The brand step has NO cross-step precondition — it is the front of the chain, and media runs in
 * parallel with it. The only thing derivable here is whether the staff input is complete enough for
 * the backend's `NormalizedBrandInputSchema` to parse.
 */
const evaluateBrandGate = (input: OnboardingBrandInput | null | undefined): QueueGate => {
  if (!isNonBlank(input?.name)) {
    return block(
      `The brand details on this step are incomplete, so nothing would be sent to Apple: a brand ` +
        `name is required. Fill it in on the Brand section and save, then queue.`,
    );
  }
  // ⚠️ SAME FAILURE SHAPE AS THE OWNERSHIP ENUM, found in the same sweep. `BrandUrlSchema.url` is
  // `z.url()` (`delegated-brand-request.schema.ts:14`), and Zod parses the WHOLE brand input — so one
  // half-filled URL row rejects the entire step as `INPUT_INVALID`. An empty row cannot be pruned
  // away either: `pruneEmptyLeaves` drops the blank leaves, and a row reduced to `{ type: … }` still
  // fails. Refusing here with the reason beats a five-minute round trip to the same answer.
  const brokenUrlRow = (input?.urls ?? []).findIndex(
    (entry) => !isNonBlank(entry?.url) || !isNonBlank(entry?.type),
  );
  if (brokenUrlRow >= 0) {
    return block(
      `URL row ${brokenUrlRow + 1} is incomplete — every row needs both a full URL (including ` +
        `https://) and a type. Apple's schema rejects the whole brand if any row is malformed, so ` +
        `complete the row or remove it, save, then queue.`,
    );
  }

  return allow(
    isNonBlank(input?.practiceType)
      ? null
      : "No practice type is set. The backend maps it to an Apple category, and an unmapped or " +
          "missing one comes back as an actionable gap rather than a silent default.",
  );
};

/**
 * Media is the one step in the chain with no cross-step precondition (it is company-scoped on
 * Apple's side, so it uploads and reviews while the brand publishes). Input completeness only.
 */
const evaluateMediaGate = (input: OnboardingMediaInput | null | undefined): QueueGate => {
  const missing: string[] = [];
  if (!isNonBlank(input?.fileName)) missing.push("a file name");
  if (!isNonBlank(input?.imageUrl)) missing.push("an image URL");
  if (missing.length > 0) {
    return block(
      `The practice image on this step is incomplete, so nothing would be sent to Apple: it needs ` +
        `${missing.join(" and ")}. Fill it in on the Practice image section and save, then queue.`,
    );
  }
  return allow(
    "The image URL is not checked here — the backend downloads it before uploading, and an " +
      "unreachable or oversized file comes back as MEDIA_UNUSABLE with no Apple call made.",
  );
};

/** Alt text is capped at 255 on the backend. Enforced here too, so the button explains it first. */
const BRAND_ASSET_ALT_TEXT_MAX = 255;

/**
 * The cover photo and place-card logo.
 *
 * ⚠️ BOTH IMAGES OR NEITHER, and that is Apple's rule rather than a form convenience. Apple renders
 * the branding as a SET, so submitting one alone puts an image through an Apple review cycle of
 * several days and displays NOTHING at the end of it. The backend refuses a lone asset with
 * `BRAND_ASSET_PAIR_INCOMPLETE` before anything is sent; blocking the button is the cheaper place to
 * say so.
 *
 * ⚠️ NO CROSS-STEP PRECONDITION. Unlike the location step, this does NOT wait for the brand to reach
 * `PUBLISHED`: assets attach to a brand that merely EXISTS, and making them wait would serialise two
 * multi-day Apple reviews that can run concurrently. It does need a brand id, which the backend reads
 * from the brand step's recorded bucket — so the only thing derivable here is input completeness.
 *
 * ⚠️ LENGTHS ARE CHECKED HERE **AND** ON THE BACKEND. This console has no test runner, so it is not
 * allowed to be the only place a rule lives; the backend's Zod schema is the authority
 * (`min(1).max(255)`). This copy exists purely so the button explains itself before it is pressed.
 */
const evaluateBrandAssetsGate = (
  input: OnboardingBrandAssetsInput | null | undefined,
): QueueGate => {
  const hasCover =
    isNonBlank(input?.coverPhoto?.imageUrl) && isNonBlank(input?.coverPhoto?.fileName);
  const hasLogo =
    isNonBlank(input?.placecardLogo?.imageUrl) && isNonBlank(input?.placecardLogo?.fileName);

  if (!hasCover || !hasLogo) {
    const missing = [!hasCover ? "the cover photo" : null, !hasLogo ? "the place-card logo" : null]
      .filter((part): part is string => Boolean(part))
      .join(" and ");
    return block(
      `Apple shows a practice's branding as a SET, so both images must be attached before anything is ` +
        `sent — this step is still missing ${missing} (each needs a file name AND an image URL). ` +
        `Sending one alone would spend several days in Apple review and then display nothing at all.`,
    );
  }

  const altText = input?.altText?.trim() ?? "";
  if (altText.length > BRAND_ASSET_ALT_TEXT_MAX) {
    return block(
      `The alt text is ${altText.length} characters; Apple's limit is ${BRAND_ASSET_ALT_TEXT_MAX}. ` +
        `Shorten it and save, then queue. (Apple's guidance is one concise sentence describing the ` +
        `business.)`,
    );
  }

  return allow(
    altText.length === 0
      ? "No alt text supplied. This does NOT block the step — Apple marks only the image and the " +
          "intent required on a brand asset, so the images go to Apple without alt text and nothing " +
          "is refused. Worth adding one concise sentence describing the practice anyway: it is what " +
          "a screen-reader user hears in place of the branding."
      : null,
    // The one thing this console genuinely cannot decide: the geometry rules are exact, and the bytes
    // are not here.
    "The images are not measured here. Apple's cover photo must be at least 1600x1040 at 1.54:1 and " +
      "the logo exactly 1024x1024 — the backend checks both from the file's own header, and asks " +
      "Apple to pre-validate them, before anything is created.",
  );
};

/**
 * ⚠️ THE LOCATION STEP'S PRECONDITION IS APPLE'S, AND IT IS HARD: `AppleLocation.rtf:532` — a
 * delegated partner may not create a location until the associated brand's resource state is
 * `PUBLISHED`. That is an Apple-side wait of days, so it is a first-class gating REASON rather than
 * an error, and the refusal wording is the backend's own `brandRefusalMessage`.
 *
 * The brand's state IS derivable here, because `brand` is one of the two mirrored steps. When the
 * mirror has not run, this allows with a caution instead of blocking — a readiness refusal costs
 * nothing (the backend resets `attempts` for every code on `REQUEUEABLE_FAILURE_CODES`, and
 * `BRAND_NOT_PUBLISHED` is one), whereas an unpressable button with a stale reason costs a
 * hand-edited document.
 */
const evaluateLocationGate = (args: {
  input: OnboardingLocationInput | null | undefined;
  pinnedCompanyId: string | null | undefined;
  brandBucket: EnvScopedBrandState | undefined;
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { input, pinnedCompanyId, brandBucket, env } = args;

  if (!isNonBlank(input?.name)) {
    return block(
      `The location details on this step are incomplete, so nothing would be sent to Apple: a ` +
        `location name is required. Fill it in on the Location section and save, then queue.`,
    );
  }

  // Same class, found in the same sweep. `NormalizedCoordinatesSchema`
  // (`delegated-location-request.schema.ts:19-22`) requires BOTH `latitude` and `longitude` as bare
  // `z.number()`. A half-filled coordinate pair rejects the whole location input — before the pruning
  // as a type error on `null`, and after it as a missing required key. Either way the button was
  // enabled and the cause invisible.
  const coordinates = input?.coordinates;
  if (
    coordinates &&
    !(isFiniteNumber(coordinates.latitude) && isFiniteNumber(coordinates.longitude))
  ) {
    return block(
      `The coordinates on this step are incomplete — Apple needs BOTH a latitude and a longitude, ` +
        `or neither. Fill in the missing one or clear both, save, then queue.`,
    );
  }

  // ⚠️ THE PRUNING MADE THIS REACHABLE, so the gate has to cover it. `NormalizedOpeningHoursSchema`
  // (`delegated-location-request.schema.ts:23-27`) requires `day`, `opensAt` AND `closesAt` as bare
  // `z.string()` — `""` used to satisfy them. `pruneFormValue` now removes the blank one, so a
  // half-filled row arrives with the key MISSING, which Zod reports as "Required" and which rejects
  // the WHOLE location input. Same failure shape as a half-filled brand URL row, same remedy.
  const brokenHoursRow = (input?.openingHours ?? []).findIndex(
    (row) => !isNonBlank(row?.day) || !isNonBlank(row?.opensAt) || !isNonBlank(row?.closesAt),
  );
  if (brokenHoursRow >= 0) {
    return block(
      `Opening-hours row ${brokenHoursRow + 1} is incomplete — every row needs a day, an opening ` +
        `time and a closing time. A partly-filled row rejects the whole location step, so complete ` +
        `the row or remove it, save, then queue.`,
    );
  }

  const brandId = brandBucket?.brandId;
  const brandCompanyId = brandBucket?.companyId;
  if (!brandId || !brandCompanyId) {
    return block(
      `This practice has no Apple brand recorded for the ${envName(env)} environment yet, and Apple ` +
        `refuses to create a location until its brand is published. Run the brand step first; the ` +
        `status updates on its own once Apple publishes the brand, and then this step can be queued.`,
    );
  }

  // The step's optional company pin must agree with where the brand actually lives, or the create
  // would carry a brand id from one Apple company to a location in another.
  if (isNonBlank(pinnedCompanyId) && pinnedCompanyId !== brandCompanyId) {
    return block(
      `This location step pins Apple company "${pinnedCompanyId}", but the brand DearDoc published ` +
        `for this practice lives under company "${brandCompanyId}". Apple will not accept a brand ` +
        `from one company on a location in another. Either clear the pinned company on this step ` +
        `(the backend resolves it from the client's grant), or re-run the brand step under the ` +
        `company you actually want this location in — then queue.`,
    );
  }

  if (isLiveMirroredEvidence(brandBucket)) {
    if (brandBucket?.state !== AppleResourceStateEnum.PUBLISHED) {
      return block(brandNotPublishedReason(brandId, brandBucket?.state));
    }
    return allow(bookingUrlCaution(input));
  }

  return allow(
    `Apple's brand state here was recorded when the brand step ran and has not been refreshed by ` +
      `the status mirror, so this console cannot confirm the brand is published. Queueing will get ` +
      `the real answer: the backend reads Apple's own record and refuses with BRAND_NOT_PUBLISHED, ` +
      `which makes no Apple call and does not consume an attempt.`,
    bookingUrlCaution(input),
  );
};

/** The booking quicklink is the whole feature; its absence is worth saying before the create. */
const bookingUrlCaution = (input: OnboardingLocationInput | null | undefined): string | null =>
  isNonBlank(input?.bookingUrl)
    ? null
    : "No booking URL is set, so the location will be created WITHOUT the Schedule quicklink — a " +
      "valid place card with no booking button, recorded afterwards as “Booking omitted”. Add one " +
      "before queueing unless that is genuinely what you want.";

/**
 * THE STEP WHOSE PRECONDITION THIS CONSOLE COULD NOT EVALUATE — AND NOW CAN.
 *
 * Apple's documented precondition is "the location is PUBLISHED" (`AppleLocation.rtf:2953`), which
 * means the client cleared claim validation and Apple ops approved it — days, and driven by the
 * client.
 *
 * ⚠️ THIS GATE WAS BUILT AGAINST A FIELD THAT COULD NOT ANSWER, AND THE SHAPE IS WORTH KEEPING. When
 * it was written, `location` was NOT in `ONBOARDING_MIRRORED_STEP_NAMES`: `location.apple.<ENV>.state`
 * was written once at create time and nothing refreshed it, so gating on it would have blocked this
 * button forever while looking like live state. Rather than hardcode "unknowable", the publication
 * check was put behind `isLiveMirroredEvidence`, which reads `mirroredAt` off the bucket
 * STRUCTURALLY. The backend has since added `location` to the mirrored list and given its bucket a
 * `mirroredAt` (`apple-delegated-onboarding.schema.ts:765`), so the check below now fires and this
 * step enforces Apple's real precondition — with no change to the branch itself.
 *
 * Both branches remain live, and the fallback is not dead code: `readLocation` in
 * `mirror-onboarding-readiness.ts` returns `undefined` when the poller has observed nothing yet,
 * leaving the create-time seed and NO `mirroredAt`. A location created minutes ago legitimately takes
 * the fallback, which is why its copy says "not yet" rather than "never".
 *
 * ⚠️ AND IT IS STILL A PROJECTION. `assertLocationCtaReadiness` re-reads `apple_delegated_locations`
 * immediately before its Apple call. This decides what staff are TOLD; it never authorizes a write.
 */
const evaluateLocationCtaGate = (args: {
  /**
   * ⚠️ THE WHOLE PARENT, NOT A PRE-NARROWED `locationBucket`, and that is the fix rather than a
   * refactor. Taking the bucket meant `null` parent and empty bucket arrived identically, so this gate
   * could not distinguish "no location exists" from "I could not look" — see `parentUnreadable`.
   * Deriving the bucket HERE is also what stops the two facts being passed as two arguments that a
   * caller could get out of step with each other.
   */
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { parent, env } = args;

  if (!parent) return parentUnreadable("this practice's Apple location");

  const locationBucket = env ? parent.location?.apple?.[env] : undefined;
  const locationId = locationBucket?.locationId;

  if (!locationId || !locationBucket?.companyId) {
    return block(
      `This practice has no Apple location recorded for the ${envName(env)} environment, so there ` +
        `is no place card to set a booking button on. Run the location step first, wait for Apple ` +
        `to publish the location, then queue this step.`,
    );
  }

  if (isLiveMirroredEvidence(locationBucket)) {
    if (locationBucket.state !== AppleResourceStateEnum.PUBLISHED) {
      return block(locationNotPublishedReason(locationId, locationBucket.state));
    }
    return allow(ctaIsUnknowableCaution);
  }

  return allow(
    `Apple has not reported on this location yet, so whether it is published cannot be determined ` +
      `from this document: the state shown is the value recorded when the location was created, and ` +
      `the status poller has not observed it since. That is normal for a location created recently. ` +
      `Queue the step to get the real answer — the backend reads Apple's own record and, if the ` +
      `client has not finished verifying their location, refuses with LOCATION_NOT_PUBLISHED and an ` +
      `explanation, making no Apple call and consuming no attempt.`,
    ctaIsUnknowableCaution,
  );
};

/**
 * ⚠️ NO MIRROR CAN EVER FIX THIS ONE, unlike the publication check above. Apple states outright that
 * the available CTA values are not knowable until Apple knows which Apple Maps location matches
 * ours, and `locationCta.apple.<ENV>.offeredCtas` is populated BY this step, not before it. That is
 * why `callToAction` is an open string rather than a dropdown.
 */
const ctaIsUnknowableCaution =
  "Which call-to-action Apple offers for this place card is only knowable from Apple's own " +
  "Get-CTAs read, which happens when this step runs — so a value cannot be validated in advance. " +
  "Leaving it empty is safe and means SCHEDULE, the booking button this feature exists for: an " +
  "empty field is OMITTED from the write rather than saved as blank, which is what the backend's " +
  "`.optional()` needs to apply its default. A value Apple does not offer comes back as " +
  "CTA_NOT_OFFERED against the real list.";

/**
 * The `locationDetails` gate — holiday hours and payment methods on a location that already exists.
 *
 * ⚠️ IT READS `parent.location.input`, NOT ITS OWN STEP'S INPUT, which is the one place in this file a
 * gate looks at another step's form. That is the ONE-INPUT-SURFACE decision
 * (`OnboardingLocationDetailsStep`): the location step's form is where these two fields are edited, and
 * this step is a second verb over the same data. A reader who expects `step?.input` here will find it
 * empty, on purpose.
 *
 * ⚠️ IT DOES NOT CHECK WHETHER THE LOCATION IS PUBLISHED, unlike the CTA gate beside it, because the
 * backend's own gate does not either: Apple documents `PUBLISHED` as the precondition for the CTA flow
 * specifically, and holding a practice's Christmas hours hostage to CLAIM VALIDATION would be a
 * refusal Apple never asked for. The failure direction if Apple does refuse is safe and loud — a 400
 * that changed nothing, re-queueable.
 *
 * ⚠️ IT REFUSES AN EMPTY SEND rather than allowing a pointless full-replace PUT. The backend refuses
 * it too; catching it here saves an attempt and, more usefully, tells the staff member the reason is
 * "there is nothing in the form" rather than leaving them to read a failure code five minutes later.
 */
const evaluateLocationDetailsGate = (args: {
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  pinnedCompanyId?: string | null;
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { parent, pinnedCompanyId, env } = args;

  if (!parent) return parentUnreadable("this practice's Apple location");

  const locationBucket = env ? parent.location?.apple?.[env] : undefined;
  const locationId = locationBucket?.locationId;
  const locationCompanyId = locationBucket?.companyId;

  if (!locationId || !locationCompanyId) {
    return block(
      `This practice has no Apple location recorded for the ${envName(env)} environment, so there are ` +
        `no details to update. Run the location step first, then queue this one.`,
    );
  }

  // The same pin-versus-reality check the location and CTA steps carry, and for the same reason: the
  // pin selects the grant and therefore the access token the update is made with.
  if (isNonBlank(pinnedCompanyId) && pinnedCompanyId !== locationCompanyId) {
    return block(
      `This step pins Apple company "${pinnedCompanyId}", but the location DearDoc created for this ` +
        `practice lives under company "${locationCompanyId}". Clear the pinned company on this step — ` +
        `the backend takes it from the location step, which is the only place it can be right — then ` +
        `queue.`,
    );
  }

  const input = parent.location?.input;
  const hasSpecialHours = (input?.specialHours ?? []).length > 0;
  const hasPaymentMethods = (input?.paymentMethods ?? []).length > 0;
  /**
   * ⚠️ `!== undefined`, NOT `.length > 0`, AND THE DIFFERENCE IS THE WHOLE OF ITEM B. An explicit empty
   * payment-method list is a real instruction — "this practice accepts none of these" — which the
   * backend sends as a `[]` that CLEARS the field on Apple. Gating on length here would block the one
   * gesture `STEP_MEANINGFUL_EMPTY_ARRAYS` exists to make expressible, and staff would be told there is
   * "nothing to send" at the exact moment they were trying to send something.
   *
   * A MISSING key still means "do not touch" on both sides, which is why the distinction survives
   * rather than being normalised away.
   */
  const clearsPaymentMethods = input?.paymentMethods !== undefined && !hasPaymentMethods;

  if (!hasSpecialHours && !hasPaymentMethods && !clearsPaymentMethods) {
    return block(
      `There are no holiday hours and no payment methods on the Location step's form, so this step ` +
        `would rewrite the location for no reason. Add holiday hours or tick some payment methods on ` +
        `the Location section, save, then queue.`,
    );
  }

  // A half-filled holiday row is the one thing this gate can catch that the staff member can fix in
  // the same breath — and it is the shape the backend refuses with INPUT_INVALID.
  const brokenHolidayRow = (input?.specialHours ?? []).findIndex(
    (row) => !isNonBlank(row?.startDate) || !isNonBlank(row?.endDate),
  );
  if (brokenHolidayRow >= 0) {
    return block(
      `Holiday hours row ${brokenHolidayRow + 1} has no first day and/or no last day. Both are ` +
        `required — repeat the first day for a single day — and the backend refuses the whole step ` +
        `without them. Fix the row on the Location section, save, then queue.`,
    );
  }

  return allow(
    clearsPaymentMethods
      ? "⚠️ Every payment method is unticked, and that is a real instruction rather than a blank: this run tells Apple the practice accepts NONE of them and the “Good to Know” payment list disappears from the place card. If you meant “leave them as they are”, that is not something this form can say once the field has been saved — re-tick what the practice accepts instead."
      : null,
    hasSpecialHours && !hasPaymentMethods && !clearsPaymentMethods
      ? "No payment methods have ever been saved for this practice, so this run leaves whatever is on Apple exactly as it is."
      : null,
    !hasSpecialHours && (hasPaymentMethods || clearsPaymentMethods)
      ? "No holiday hours are entered, so this run leaves any holiday hours on Apple exactly as they are. Clearing holiday hours is not something this step can do — an entry stops showing on its own once its last day has passed."
      : null,
    "New or changed holiday entries appear on the place card only about a week ahead, so an entry for December 24th will not be visible until roughly December 18th even once this step succeeds.",
  );
};

/**
 * The showcase gate — the one surface whose inputs are produced by OTHER steps, and the one where a
 * mistyped id does real damage.
 *
 * ⚠️ THE ID PINS ARE THE POINT. `input.locationId` is Apple's id, staff-typed, and it flows into the
 * showcase payload as the resource being decorated. A mistyped id belonging to ANOTHER of this same
 * client's locations puts a real, live showcase on the wrong storefront, and Apple's showcase
 * endpoints take no client-supplied key, so undoing it is manual. The backend pins it against
 * `location.apple.<ENV>.locationId` — a BACKEND-OWNED field, which is the whole reason it can serve
 * as a pin — and this repeats that check so the answer arrives before the write, not after it.
 */
/**
 * The verdict for "we could not read the parent onboarding document", shared by every gate whose
 * preconditions live on that parent.
 *
 * ⚠️ IT EXISTS BECAUSE TWO GATES USED TO CONFLATE "UNREADABLE" WITH "ABSENT", AND SAID SO OUT LOUD.
 * `fetchOnboardingParent` ends in `catch { return null }`, so a transient Firestore read failure hands
 * a gate exactly what a genuinely empty bucket hands it: `undefined`. The gallery-photo and
 * place-card-CTA gates took only the already-narrowed `locationBucket`, could not tell the two apart,
 * and blocked with "This practice has no Apple location recorded for the … environment" — a sentence
 * asserting a fact the console never established. It had only established that it could not look.
 *
 * ⚠️ WHY THE ANSWER IS `allow`-WITH-CAUTION RATHER THAN A BLOCK. The console is not the safety
 * boundary; the backend re-gates every one of these server-side against the authoritative record
 * (`assertGalleryPhotoReadiness`, `assertLocationCtaReadiness`, `assertShowcaseReadiness`) and refuses
 * with no Apple call if the precondition is genuinely unmet. So blocking here buys no safety and costs
 * a staff member the ability to queue a perfectly ready step, while telling them something untrue
 * about their client's data. Saying "I could not check, and here is what still will be" is both honest
 * and strictly more useful.
 *
 * ⚠️ EACH GATE CALLS THIS AT ITS OWN POINT IN ITS OWN ORDERING, deliberately, rather than the check
 * being hoisted ahead of the `switch` in `evaluatePerStepGate`. A gate's INPUT-SHAPE blocks are
 * knowable without the parent and are the more actionable message when both apply — a photo with no
 * caption should be told about the caption, not about a read failure. Hoisting would have reordered
 * that for `showcase`, which already got it right.
 *
 * @param unchecked what this gate would have verified had the parent been readable, as a noun phrase.
 *   Worded so the sentence below reads correctly whether it names one thing or several — hence "could
 *   NOT be checked" rather than a was/were the caller would have to get right.
 */
const parentUnreadable = (unchecked: string): QueueGate =>
  allow(
    `The parent onboarding document could not be read from here, so ${unchecked} could NOT be ` +
      `checked. The backend re-checks this itself before it calls Apple and refuses if anything ` +
      `disagrees — but you are queueing without the usual safety net.`,
  );

const evaluateShowcaseGate = (args: {
  input: OnboardingShowcaseInput | null | undefined;
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { input, parent, env } = args;

  const missing = SHOWCASE_REQUIRED_FIELDS.filter(({ key }) => !isNonBlank(input?.[key]));
  if (missing.length > 0) {
    return block(
      `The showcase details on this request are incomplete, so nothing would be sent to Apple. ` +
        `Missing: ${missing.map(({ label }) => label).join(", ")}. Fill them in and save, then queue.`,
    );
  }

  if (!parent) return parentUnreadable("the brand, location and image pins");

  const brandBucket = env ? parent.brand?.apple?.[env] : undefined;
  if (!brandBucket?.brandId || !brandBucket.companyId) {
    return block(
      `This practice has no Apple brand recorded for the ${envName(env)} environment yet, so there ` +
        `is nothing to attach a showcase to. Run the brand step first and wait for Apple to publish ` +
        `it, then queue this showcase.`,
    );
  }
  if (input?.brandId !== brandBucket.brandId) {
    return block(
      `This showcase names brand "${input?.brandId}", but the brand DearDoc created for this ` +
        `location is "${brandBucket.brandId}". We will not write a showcase to a brand we did not ` +
        `create here. Correct the brand id to match the brand step, then queue.`,
    );
  }
  if (isDeadBrandState(brandBucket.contractState)) {
    return block(
      `Apple has ${
        brandBucket.contractState === DelegatedBrandContractStateEnum.REJECTED
          ? "rejected"
          : "failed to process"
      } this practice's brand, so a showcase on it cannot go live. Fix the brand step first, then ` +
        `queue this showcase.`,
    );
  }

  const recordedLocationId = env ? parent.location?.apple?.[env]?.locationId : undefined;
  if (!recordedLocationId) {
    return block(
      `This practice has no Apple location recorded for the ${envName(env)} environment, so we ` +
        `cannot confirm that the location this showcase names is the right one. Run the location ` +
        `step first, then queue this showcase. (If the location was created in Apple Business ` +
        `Connect by hand rather than by DearDoc, engineering has to record its Apple id first — a ` +
        `typed id is not taken on trust, because a wrong one puts a live showcase on the wrong ` +
        `storefront.)`,
    );
  }
  if (input?.locationId !== recordedLocationId) {
    return block(
      `This showcase names Apple location "${input?.locationId}", but the location DearDoc created ` +
        `for this practice is "${recordedLocationId}". We will not put a showcase on a storefront we ` +
        `did not create here — a mistyped id belonging to another of this client's locations would ` +
        `publish a real showcase on the wrong place card, and Apple gives us no way to undo it. ` +
        `Correct the location id to match the location step, then queue.`,
    );
  }

  return allow(...showcaseMediaAdvice(input, env ? parent.media?.apple?.[env] : undefined));
};

const SHOWCASE_REQUIRED_FIELDS: { key: keyof OnboardingShowcaseInput; label: string }[] = [
  { key: "accountId", label: "DearDoc account ID" },
  { key: "locationId", label: "Apple location ID" },
  { key: "brandId", label: "Apple brand ID" },
  { key: "mediaId", label: "Apple image ID" },
  { key: "headline", label: "headline" },
  { key: "body", label: "body" },
  { key: "altText", label: "alt text" },
  { key: "startDate", label: "start date" },
  { key: "endDate", label: "end date" },
];

/**
 * Apple refuses a creative built on a non-approved image, and approval is asynchronous (~3 business
 * days). This is a CAUTION rather than a block: the mirror answers for the image the MEDIA STEP
 * uploaded, and a showcase may legitimately name a different image id that this document knows
 * nothing about. The backend's gate reads the authoritative record for whichever id is named.
 */
const showcaseMediaAdvice = (
  input: OnboardingShowcaseInput | null | undefined,
  mediaBucket: EnvScopedMediaState | undefined,
): (string | null)[] => {
  if (!mediaBucket?.mediaId) {
    return [
      "No practice image is recorded for this location, so the image this showcase names cannot be " +
        "checked here. Apple refuses a creative built on an image it has not APPROVED; the backend " +
        "verifies that before calling Apple and refuses with MEDIA_NOT_APPROVED if it has not.",
    ];
  }
  if (input?.mediaId !== mediaBucket.mediaId) {
    return [
      `This showcase names image "${input?.mediaId}", but the image DearDoc uploaded for this ` +
        `location is "${mediaBucket.mediaId}". That may be deliberate — but its approval state is ` +
        `not on this document, so the backend's check is the only one that will see it.`,
    ];
  }
  if (!isLiveMirroredEvidence(mediaBucket)) {
    return [
      "Apple's image state here was recorded at upload time and has not been refreshed by the " +
        "status mirror, so approval cannot be confirmed from this document. The backend reads " +
        "Apple's own record and refuses with MEDIA_NOT_APPROVED if the image is not approved.",
    ];
  }
  if (mediaBucket.state !== AppleMediaStateEnum.APPROVED) {
    return [mediaNotApprovedAdvice(mediaBucket.mediaId, mediaBucket.state)];
  }
  return [];
};

/**
 * ONE GALLERY PHOTO — the second subcollection's gate.
 *
 * ⚠️ ITS CROSS-STEP PRECONDITION IS THE LOCATION, AND IT IS A HARD ONE. A gallery photo is a
 * LOCATION ASSET: it hangs off Apple's own location id, which the backend takes from the location
 * step's env-scoped bucket and never from anything staff typed (see `DelegatedGalleryPhotoRequest`
 * in `services/delegated-location-assets/delegated-location-asset.types.ts`). With no recorded
 * location there is nothing to attach a photo to, so this blocks rather than cautions.
 *
 * ⚠️ IT DOES **NOT** CHECK THAT THE LOCATION IS PUBLISHED, and that omission is deliberate rather
 * than an oversight. Whether Apple accepts assets on a location that is not yet published is not
 * something this repo can answer — no Apple document is on this machine, and the backend's gallery
 * readiness gate is not in the branch this was written against — so a block would be an invented
 * rule and a confident-sounding caution would be an invented fact. The unpublished case gets one
 * honest line instead: this console cannot tell, and the backend's answer is the real one.
 *
 * ⚠️ AND NOTHING HERE CHECKS THE IMAGE ITSELF. Geometry, format and byte size are re-derived by the
 * backend from the file's own header before any Apple call, and Apple pre-validates on top of that.
 * The cropper enforces the shape at attach time; this gate only says what a staff member can act on.
 */
const evaluateGalleryPhotoGate = (args: {
  input: OnboardingGalleryPhotoInput | null | undefined;
  pinnedCompanyId: string | null | undefined;
  /** The whole parent, for the reason `evaluateLocationCtaGate` states — see `parentUnreadable`. */
  parent: Partial<AppleDelegatedOnboardingEntity> | null;
  env: AppleEnvironmentEnum | null;
}): QueueGate => {
  const { input, pinnedCompanyId, parent, env } = args;

  const missing: string[] = [];
  if (!isNonBlank(input?.photo?.fileName)) missing.push("a file name");
  if (!isNonBlank(input?.photo?.imageUrl)) missing.push("an image");
  if (missing.length > 0) {
    return block(
      `This gallery photo is incomplete, so nothing would be sent to Apple: it needs ` +
        `${missing.join(" and ")}. Attach the photo on the Photo section and save, then queue.`,
    );
  }

  const captions = input?.captions ?? [];
  if (captions.length === 0) {
    return block(
      `This photo has no caption, and Apple requires at least one. Add a caption row with both a ` +
        `title and alt text, save, then queue.`,
    );
  }

  // ⚠️ SAME FAILURE SHAPE AS A HALF-FILLED BRAND URL ROW, and worth refusing here for the same
  // reason. `title` and `altText` are both `z.string().min(1)` WITHIN a caption
  // (`GalleryPhotoCaptionInputSchema`), and Zod parses the whole input — so one half-filled row
  // rejects the entire photo as `INPUT_INVALID`. An empty row cannot be pruned away either: the
  // pruning drops blank leaves, and a row reduced to `{ locale: … }` still fails.
  const brokenCaptionRow = captions.findIndex(
    (row) => !isNonBlank(row?.title) || !isNonBlank(row?.altText),
  );
  if (brokenCaptionRow >= 0) {
    return block(
      `Caption row ${brokenCaptionRow + 1} is incomplete — every row needs BOTH a title and alt ` +
        `text. Apple's schema rejects the whole photo if any row is half-filled, so complete the ` +
        `row or remove it, save, then queue.`,
    );
  }

  // ⚠️ AFTER the input and caption blocks, BEFORE the location one. A photo whose captions are broken
  // should be told about the captions — that is knowable without the parent and is the more actionable
  // message. Only once the input is sound does an unreadable parent become the thing worth saying.
  if (!parent)
    return parentUnreadable("this practice's Apple location and this photo's company pin");

  const locationBucket = env ? parent.location?.apple?.[env] : undefined;
  const locationId = locationBucket?.locationId;
  const locationCompanyId = locationBucket?.companyId;
  if (!locationId || !locationCompanyId) {
    return block(
      `This practice has no Apple location recorded for the ${envName(env)} environment, so there ` +
        `is no place card to put a photo on. Run the location step first, then queue this photo.`,
    );
  }

  // The photo's optional company pin must agree with where the location actually lives: an Apple
  // location id from one company cannot be addressed under another, so a mismatch is a write aimed
  // at the wrong client's company rather than a failed lookup.
  if (isNonBlank(pinnedCompanyId) && pinnedCompanyId !== locationCompanyId) {
    return block(
      `This photo pins Apple company "${pinnedCompanyId}", but the location DearDoc created for ` +
        `this practice lives under company "${locationCompanyId}". A location id from one company ` +
        `cannot be addressed under another. Clear the pinned company on this photo — the backend ` +
        `resolves it from the client's grant — then queue.`,
    );
  }

  return allow(
    // Recorded per photo by the backend, so it is worth saying that the number staff can see is a
    // snapshot rather than Apple's live count.
    `Apple caps a location at 100 gallery photos and counts what is already there when this runs, ` +
      `so a full gallery is refused with the count and the cap rather than silently dropped. The ` +
      `photo itself is re-checked from the file before any Apple call: an unreachable or wrongly ` +
      `shaped image comes back as an actionable failure with no Apple call made.`,
    isLiveMirroredEvidence(locationBucket)
      ? null
      : `Apple has not reported on this location since it was created, so this console cannot ` +
          `confirm the place card is live. That is normal for a location created recently. Queueing ` +
          `gets the real answer from Apple; nothing here is taken on trust by the backend.`,
  );
};

const isDeadBrandState = (state: unknown): boolean =>
  state === DelegatedBrandContractStateEnum.REJECTED ||
  state === DelegatedBrandContractStateEnum.FAILED;

/* --------------------------------- refusal copy, keyed on state ------------------------------- */

/** The backend's `brandRefusalMessage`, with the "nothing was sent" tail restated for a pre-check. */
const brandNotPublishedReason = (
  brandId: string,
  state: AppleResourceStateEnum | null | undefined,
): string => {
  const tail = "Nothing has been queued.";
  switch (state) {
    case AppleResourceStateEnum.SUBMITTED:
    case AppleResourceStateEnum.PROCESSING:
    case AppleResourceStateEnum.IN_REVIEW:
    case AppleResourceStateEnum.APPROVED:
      return (
        `Apple has not published this practice's brand yet (brand ${brandId} is ` +
        `"${stateLabel(state)}"), and it refuses to create a location until the brand's state is ` +
        `PUBLISHED. Nobody needs to do anything — the status updates on its own, and this step can ` +
        `be queued once it reads Published. ${tail}`
      );
    case AppleResourceStateEnum.REJECTED:
      return (
        `Apple rejected this practice's brand (brand ${brandId}), so no location can be created ` +
        `under it. Fix the brand step and get a brand published first, then queue this location. ${tail}`
      );
    case AppleResourceStateEnum.FAILED:
      return (
        `Apple accepted this practice's brand but could not process it (brand ${brandId} is FAILED), ` +
        `so it will never publish as it stands. Fix the brand step first, then queue this location. ${tail}`
      );
    default:
      return (
        `We cannot confirm that Apple has published this practice's brand (brand ${brandId} is ` +
        `"${state ?? "not recorded for this environment"}"), and Apple refuses a location create ` +
        `until the brand's state is PUBLISHED. Check the brand step. ${tail}`
      );
  }
};

/** The backend's `locationRefusalMessage`. Unreachable until the location mirror ships — by design. */
const locationNotPublishedReason = (
  locationId: string,
  state: AppleResourceStateEnum | null | undefined,
): string => {
  const tail = "Nothing has been queued.";
  switch (state) {
    case AppleResourceStateEnum.SUBMITTED:
    case AppleResourceStateEnum.PROCESSING:
    case AppleResourceStateEnum.IN_REVIEW:
      return (
        `Apple has not published this location yet (location ${locationId} is "${stateLabel(state)}"), ` +
        `and the booking button can only be set on a published place card. This is usually waiting ` +
        `on the CLIENT: Apple asks them to verify the location by phone or by uploading documents, ` +
        `and then Apple's own team reviews it. Chase the client if it has been a while. ${tail}`
      );
    case AppleResourceStateEnum.REJECTED:
      return (
        `Apple rejected this location (location ${locationId}), so it has no place card to put a ` +
        `booking button on. Fix the location step first. ${tail}`
      );
    case AppleResourceStateEnum.FAILED:
      return (
        `Apple accepted this location but could not process it (location ${locationId} is FAILED), ` +
        `so it will not publish as it stands. Engineering needs to look at the location step's ` +
        `Apple reports. ${tail}`
      );
    default:
      return (
        `We cannot confirm that Apple has published this location (location ${locationId} is ` +
        `"${state ?? "not recorded for this environment yet"}"), and the booking button can only be ` +
        `set on a published place card. ${tail}`
      );
  }
};

/** The backend's `mediaRefusalMessage`, shown as advice because the pin may name another image. */
const mediaNotApprovedAdvice = (
  mediaId: string,
  state: AppleMediaStateEnum | null | undefined,
): string => {
  switch (state) {
    case AppleMediaStateEnum.SUBMITTED:
    case AppleMediaStateEnum.IN_REVIEW:
      return (
        `Apple is still reviewing this practice image (image ${mediaId}), and it refuses a showcase ` +
        `built on an image it has not approved. Image review typically takes a few business days — ` +
        `the status updates on its own, and this showcase can be queued once it reads Approved.`
      );
    case AppleMediaStateEnum.REJECTED:
      return (
        `Apple rejected this practice image (image ${mediaId}), so no showcase can use it. Upload a ` +
        `different image on the media step, then point this showcase at the new image.`
      );
    case AppleMediaStateEnum.DELETED:
      return (
        `This practice image (image ${mediaId}) has been deleted in Apple Business Connect. Upload ` +
        `a replacement on the media step, then queue this showcase.`
      );
    default:
      return (
        `We have no approved practice image on record for image "${mediaId}" under this client's ` +
        `company in this environment, so a showcase creative cannot be built. Run the media step and ` +
        `wait for Apple to approve the image.`
      );
  }
};

const stateLabel = (
  state: AppleResourceStateEnum | AppleMediaStateEnum | null | undefined,
): string =>
  (state && (APPLE_RESOURCE_STATE_LABELS[state] ?? APPLE_MEDIA_STATE_LABELS[state])) ??
  String(state ?? "unknown");

const envName = (env: AppleEnvironmentEnum | null): string => env ?? "current";

const isFiniteNumber = (value: unknown): boolean =>
  typeof value === "number" && Number.isFinite(value);

const isNonBlank = (value: unknown): boolean =>
  typeof value === "string" ? value.trim().length > 0 : value !== undefined && value !== null;

/* ------------------------------------------------------------------------------------------------
 * THE WRITE BOUNDARY — the only shapes this console ever sends to Firestore
 * ---------------------------------------------------------------------------------------------- */

/**
 * The exact values object for "staff pressed the queue button".
 *
 * ⚠️ THREE KEYS, ALL CMS-OWNED OR SHARED, AND NOTHING ELSE. `submitState` is the one shared field
 * and `QUEUED` is one of the two values `CMS_WRITABLE_SUBMIT_STATES` permits. `queuedAt` and
 * `writeError` are on `ONBOARDING_STEP_CMS_OWNED_FIELDS`. Nothing here touches `attempts`,
 * `failureCode`, `failureMessage`, `claimedAt`, `completedAt` or any `apple` bucket — resetting
 * `attempts` is how a duplicate live Apple resource gets minted, and writing an `apple` bucket would
 * defeat the id pins the showcase gate depends on.
 *
 * `writeError: null` clears a previous "could not even enqueue" note. That field is CMS-owned and
 * the backend never reads it, so clearing it is not a way to dismiss a real failure — `failureCode`
 * and `failureMessage` are backend-owned and are left exactly as they are, to be overwritten by the
 * drain on its next claim.
 *
 * Returned as a nested object under the step name (or flat, for a showcase request, whose status
 * fields sit at its document ROOT) because the Firestore write is a `setDoc(..., { merge: true })`,
 * which merges maps recursively — so `{ brand: { submitState } }` touches exactly that one field.
 */
export const buildQueueIntentValues = (
  step: OnboardingStepName | null,
  now: Date,
): Record<string, unknown> =>
  wrapStepValues(step, {
    submitState: OnboardingSubmitStateEnum.QUEUED,
    queuedAt: now,
    writeError: null,
  });

/**
 * The values object for "re-open a parked step so it can be edited and queued again".
 *
 * ⚠️ THE DELIBERATE, TWO-PRESS PATH OUT OF `NEEDS_ATTENTION`, and it is deliberately NOT a retry:
 * it writes `DRAFT` and nothing else. In particular it does NOT clear `failureCode` /
 * `failureMessage` — those are backend-owned, they are the record of what went wrong, and the drain
 * overwrites them itself on its next claim. Clearing them here would erase the only evidence a
 * human has that a duplicate might exist.
 *
 * It also does not reset `attempts`, so a step at the cap stays at the cap and re-opening it changes
 * nothing about the block. That is correct: the cap is the guard against repeated non-idempotent
 * Apple writes, and only the backend may lift it.
 */
export const buildReopenIntentValues = (step: OnboardingStepName | null): Record<string, unknown> =>
  wrapStepValues(step, { submitState: OnboardingSubmitStateEnum.DRAFT });

/**
 * The values object for "the queue write itself failed".
 *
 * `writeError` exists on the contract for exactly this — "I could not even enqueue" — and the
 * backend never reads it. Written on its own so a failed enqueue leaves a visible trace instead of
 * only a snackbar the staff member has already dismissed.
 */
export const buildWriteErrorValues = (
  step: OnboardingStepName | null,
  message: string,
): Record<string, unknown> => wrapStepValues(step, { writeError: message.slice(0, 500) });

/**
 * Nests a step's values under its key, or leaves them flat for a showcase request.
 *
 * ⚠️ A showcase request's status fields sit at the DOCUMENT ROOT (`submitState`, not
 * `showcase.submitState`) — one request per document, so there is nothing to nest under. Passing
 * `null` as the step name is how a caller says "this is a showcase request".
 */
const wrapStepValues = (
  step: OnboardingStepName | null,
  values: Record<string, unknown>,
): Record<string, unknown> => (step === null ? values : { [step]: values });

/**
 * Reduces a set of form values to the fields the DETAIL FORM is allowed to write. Wired as the
 * collection's `onPreSave`, whose return value IS what gets saved.
 *
 * ⚠️ THIS IS THE STRUCTURAL HALF OF THE WRITE BOUNDARY, and `readOnly: true` is only the other half.
 * FireCMS seeds a form with `entity.values` VERBATIM — including keys that are not declared as
 * properties — and submits all of it. `readOnly` stops a human CHANGING a field; it does not stop
 * the save echoing the loaded value back. For a field the backend is concurrently writing —
 * `attempts` during a claim, an `apple` bucket during a mirror pass, `submitState` the moment the
 * drain flips it to `PROCESSING` — echoing a stale value back is a real clobber of a fresher write.
 * After this runs, a form save cannot NAME a forbidden path, whatever the properties say.
 *
 * Two rules, and the difference between them is deliberate:
 *  - inside each embedded step map, a WHITELIST (`input`, `companyId`), so a field added to the
 *    backend schema later is excluded by default rather than silently becoming form-writable;
 *  - at the document ROOT, a DENYLIST, because the root of a SHOWCASE REQUEST is itself a step
 *    (its status fields sit there) while the root of an onboarding document holds the parent's own
 *    identity fields, which a whitelist would delete.
 *
 * A third rule sits INSIDE the whitelisted values — see `pruneFormValue`. It only ever removes or
 * blanks keys already present there, so it cannot widen what this function permits.
 *
 * Returns a new object; the input is not mutated.
 */
/**
 * Step-relative field paths whose EMPTY ARRAY is a meaningful instruction rather than "untouched".
 *
 * ⚠️ WHY THIS EXISTS. `pruneFormValue` maps an empty array to `omit`, and omitting a key from a
 * `{ merge: true }` write leaves the STORED value alone. For almost every field that is right — an
 * untouched multi-select should not clear anything. For "Good to Know" it is actively harmful: a staff
 * member who unticks "Wheelchair Accessible" because the ramp was removed would save, see nothing
 * change, and Apple Maps would go on publishing a false accessibility claim about a real clinic.
 *
 * So this list opts specific fields out: an empty array is written EXPLICITLY, and the backend reads
 * `[]` as "we no longer assert any of these" (versus an absent key, which still means "do not touch").
 * It retracts only paths DearDoc previously asserted, so a client's own portal edits and Apple's data
 * providers' entries are unaffected.
 *
 * ⚠️ `pruneFormValue` ITSELF IS DELIBERATELY UNCHANGED. It is shared by every step's input, and
 * flipping its empty-array rule globally would silently give five other fields clear-on-empty
 * semantics nobody asked for. This is a post-pass over named paths instead.
 *
 * ⚠️ PATHS ARE STEP-QUALIFIED — `<step>.<container>.<key>`, three segments — and the step segment is
 * the point. A two-segment `input.locationAttributes` was applied inside the loop over EVERY step, so
 * it opted that key in on all five. Inert today (no other step has the key), but it made the mechanism
 * exactly as global as the thing this list exists to avoid: it is supposed to opt ONE field out of the
 * shared empty-array rule, not one field NAME everywhere.
 *
 * ⚠️ A brand-new record cannot reach the "absent" branch through the CMS: FireCMS defaults an array
 * property to `[]`, so a newly created document writes an explicit `locationAttributes: []`. Harmless
 * — nothing has been asserted yet, so an empty selection retracts nothing — but do not read the
 * absent/empty split below as fully reachable from the UI. The backend still needs both, because a
 * document written by anything other than this form can legitimately omit the key.
 */
const STEP_MEANINGFUL_EMPTY_ARRAYS: readonly string[] = [
  "locationCta.input.locationAttributes",
  /**
   * ⚠️ UNTICKING EVERY PAYMENT METHOD HAS TO MEAN SOMETHING, AND WITHOUT THIS LINE IT MEANT NOTHING.
   * `pruneFormValue` maps an empty array to `omit`, and omitting a key from a `{ merge: true }` write
   * leaves the STORED value alone — so a staff member who unticked every box would save, see the form
   * come back empty, and the `locationDetails` step would go on sending the OLD list to Apple. The
   * place card would keep advertising a card the practice stopped taking, from a form that shows
   * nothing ticked.
   *
   * ⚠️ AND AN EXPLICIT `[]` IS ACTUALLY ACTIONABLE FOR THIS FIELD, which is why it earns the opt-out
   * where `specialHours` does not: `paymentMethods` is a WHOLE-ARRAY REPLACE on the backend (a flat
   * scalar list has no per-entry identity to merge on), so `[]` really does clear it on Apple.
   *
   * `restoreMeaningfulEmptyArrays` only re-inserts the `[]` when the FORM genuinely held one, read
   * from the original step values — so this cannot invent a clear for a field nobody touched.
   */
  "location.input.paymentMethods",
];

/**
 * Re-inserts an explicit `[]` for the paths above, when the FORM genuinely held an empty array.
 *
 * Runs after the prune, and only ever adds back a value the form actually had — it cannot invent a
 * clear for a field the user never touched, because it reads the original step values to decide.
 */
const restoreMeaningfulEmptyArrays = (
  step: string,
  original: Record<string, unknown>,
  kept: Record<string, unknown>,
): void => {
  for (const path of STEP_MEANINGFUL_EMPTY_ARRAYS) {
    const [pathStep, container, key] = path.split(".");
    if (pathStep !== step) continue;
    const originalContainer = original[container];
    if (!isPlainObject(originalContainer)) continue;
    const value = originalContainer[key];
    if (!Array.isArray(value) || value.length > 0) continue;

    // The prune may have removed the container along with everything in it; recreate it so the
    // explicit clear survives.
    const keptContainer = isPlainObject(kept[container])
      ? (kept[container] as Record<string, unknown>)
      : {};
    keptContainer[key] = [];
    kept[container] = keptContainer;
  }
};

export const stripFieldsTheFormMayNotWrite = <T extends Record<string, unknown>>(values: T): T => {
  const stripped: Record<string, unknown> = { ...values };

  // Root — serves the showcase subcollection, plus the parent's backend-written housekeeping.
  for (const field of ONBOARDING_STEP_NON_FORM_FIELDS) delete stripped[field];
  delete stripped.updatedAt;

  // A showcase request keeps `input` / `companyId` at its ROOT, so prune them here too. On a parent
  // onboarding document neither key exists and this is a no-op. ⚠️ The parent's own root strings
  // (`salesforce_id`, `notes`) are deliberately NOT pruned: the backend types those `.nullish()`, so
  // an empty one is legal and writing `""` is the correct way to clear a note.
  pruneWhitelistedInPlace(stripped);

  for (const step of ONBOARDING_STEP_NAMES) {
    const stepValues = stripped[step];
    if (!isPlainObject(stepValues)) continue;
    const kept: Record<string, unknown> = {};
    for (const field of ONBOARDING_STEP_FORM_OWNED_FIELDS) {
      if (stepValues[field] !== undefined) kept[field] = stepValues[field];
    }
    pruneWhitelistedInPlace(kept);
    // ⚠️ AFTER the prune, and before the emptiness check: a step whose ONLY change is "clear the
    // amenities" must still be written, not dropped for looking empty.
    restoreMeaningfulEmptyArrays(step, stepValues, kept);
    // An untouched step contributes nothing rather than an empty map, so the merge write stays as
    // small as it can be.
    if (Object.keys(kept).length === 0) delete stripped[step];
    else stripped[step] = kept;
  }

  return stripped as T;
};

/**
 * Applies `pruneFormValue` to the two form-owned keys, wherever they sit. Mutates its argument.
 *
 * ⚠️ `= undefined` AND `delete` NOW MEAN OPPOSITE THINGS, and confusing them would be severe. An
 * explicit `undefined` reaches Firestore as `deleteField()` and REMOVES the field; a deleted key is
 * simply absent from the merge write and LEAVES THE STORED VALUE ALONE. So:
 *
 *  - "staff emptied this scalar"        → `undefined` → actually cleared;
 *  - "not ours to write" (the denylist) → `delete`    → untouched, which is the whole write boundary.
 *
 * ⚠️ AND `undefined` IS ONLY EVER ASSIGNED TO A SCALAR. A `deleteField()` sentinel placed on a
 * MAP-typed property would be walked into by FireCMS's `updateDateAutoValues`, whose
 * `traverseValuesProperties` does `{ ...inputValues, ...updatedValues }` — and spreading the sentinel
 * yields `{ _methodName: "deleteField" }`, which is one own enumerable key, so it survives the
 * emptiness check and would be written to Firestore as a literal garbage map instead of deleting
 * anything. Verified by reading `@firecms/core/dist/index.es.js` and by inspecting the sentinel
 * directly. A container that prunes away therefore keeps the old `delete` behaviour.
 */
const pruneWhitelistedInPlace = (target: Record<string, unknown>): void => {
  for (const field of ONBOARDING_STEP_FORM_OWNED_FIELDS) {
    if (!(field in target)) continue;
    const result = pruneFormValue(target[field]);
    if (result.action === "keep") target[field] = result.value;
    // A scalar the staff emptied — `companyId` is the one that reaches this at step level.
    else if (result.action === "clear") target[field] = undefined;
    // A whole map/array that pruned away. Left absent rather than sentinel-deleted; see above.
    else delete target[field];
  }
};

/**
 * What should happen to one form value.
 *
 * `clear` and `omit` are deliberately distinct: the first is "write a deletion", the second is "do
 * not mention this path at all". Collapsing them is how the map-sentinel corruption described on
 * `pruneWhitelistedInPlace` would get reintroduced.
 */
type PrunedFormValue =
  | { action: "keep"; value: unknown }
  | { action: "clear" }
  | { action: "omit" };

/**
 * Recursively decides, for every leaf of a form-owned value, whether it survives, is cleared, or is
 * left unmentioned.
 *
 * ⚠️ WHY THIS EXISTS: the backend's optional input fields are `.optional()`, i.e. `T | undefined`.
 * They accept the key being ABSENT — not present-and-empty. FireCMS's string field saves
 * `event.target.value` and its clear button calls `setValue(null)`, so an untouched or cleared
 * optional arrives as `""` or `null`. Three make that fatal rather than cosmetic:
 *
 *  - `locationCta.input.callToAction` — `z.string().min(1).optional()` (schema:532)
 *  - `brand.input.partnersBrandId`    — `z.string().min(1).optional()` (`delegated-brand-request.schema.ts:27`)
 *  - `location.input.status`          — `z.enum(['OPEN','CLOSED']).optional()`, NOT `.nullable()`
 *
 * `""` fails `.min(1)` and `null` fails the type, and because Zod parses the WHOLE input object one
 * empty optional rejects the entire step as `INPUT_INVALID`.
 *
 * ⚠️ A CLEARED SCALAR IS GENUINELY CLEARED, which an earlier version of this file wrongly said was
 * impossible. `@firecms/core`'s `saveEntity` calls `cmsToDelegateModel` before the write, and
 * `cmsToFirestoreModel` opens `if (data === void 0) { return deleteField(); }` — recursing through
 * objects — so an explicit `undefined` becomes a real Firestore field deletion inside the
 * `{ merge: true }` write, with no `firebase/firestore` import anywhere in this repo.
 *
 * ⚠️ IT NEVER WIDENS THE STRIP. It can return only values reachable from its own input, never a new
 * key name: object recursion iterates `Object.entries` of the value it was handed. It runs solely on
 * values the whitelist has ALREADY accepted as form-owned. And `Date` is a leaf, so a date value can
 * never be walked into and dismantled.
 */
const pruneFormValue = (value: unknown): PrunedFormValue => {
  if (value === null) return { action: "clear" };
  if (typeof value === "string") {
    return value.trim().length === 0 ? { action: "clear" } : { action: "keep", value };
  }
  if (value instanceof Date) return { action: "keep", value };

  if (Array.isArray(value)) {
    // Array ELEMENTS cannot be individually deleted in Firestore — the array is rewritten whole — so
    // a cleared element is dropped rather than turned into a sentinel.
    const kept = value
      .map(pruneFormValue)
      .filter((entry): entry is { action: "keep"; value: unknown } => entry.action === "keep")
      .map((entry) => entry.value);
    return kept.length === 0 ? { action: "omit" } : { action: "keep", value: kept };
  }

  if (isPlainObject(value)) {
    const kept: Record<string, unknown> = {};
    let mentioned = false;
    for (const [key, child] of Object.entries(value)) {
      // `__proto__` as an OWN key (only reachable via `JSON.parse`, never from a FireCMS form, whose
      // property names come from the collection definition) would assign through the prototype rather
      // than create an own key — silently losing the field. Skipped rather than special-cased.
      if (key === "__proto__") continue;
      const result = pruneFormValue(child);
      if (result.action === "keep") {
        kept[key] = result.value;
        mentioned = true;
      } else if (result.action === "clear") {
        // An explicit `undefined` — a deletion to perform, so the object is NOT empty.
        kept[key] = undefined;
        mentioned = true;
      }
      // `omit` — a nested container that pruned away. Not mentioned; see `pruneWhitelistedInPlace`.
    }
    return mentioned ? { action: "keep", value: kept } : { action: "omit" };
  }

  // Numbers, booleans and anything else the form can hold pass through untouched. `0` and `false`
  // are meaningful values (a latitude of 0 is a real coordinate) and must never be pruned.
  return { action: "keep", value };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);

/**
 * Splits `apple_delegated_onboarding/{docId}/<subcollection>` into the parent's path and id, for
 * EITHER of the two status-at-the-root subcollections.
 *
 * A showcase request's gate needs the PARENT's `brand.apple.<ENV>.brandId` and
 * `location.apple.<ENV>.locationId` to repeat the id pins, and a gallery photo's gate needs the
 * location bucket for the same reason — neither FireCMS's entity-action props nor its custom-view
 * props hand a subcollection the parent's values, only the path. Returns `null` for anything that is
 * not a child of this collection, so a caller degrades to "pins not checked here" instead of
 * fetching something arbitrary.
 *
 * ⚠️ THE SUBCOLLECTION NAME IS STILL CHECKED, against a LIST rather than one value. Dropping the
 * check to "any three-segment path under this collection" would be shorter and would also make this
 * fetch a parent for a subcollection nobody has thought about yet; the whole value of the check is
 * that a path this console does not recognise degrades loudly-in-code rather than quietly-in-UI.
 * `lifecycle_requests` is five segments deep and is unaffected either way.
 */
export const parseOnboardingParentRef = (
  childPath: string | null | undefined,
): { path: string; entityId: string } | null => {
  if (!childPath) return null;
  const segments = childPath.split("/").filter(Boolean);
  // Expected: [collection, docId, subcollection]
  if (segments.length !== 3) return null;
  if (segments[0] !== APPLE_DELEGATED_ONBOARDING_PATH) return null;
  if (!ONBOARDING_PARENT_FETCHING_SUBCOLLECTIONS.includes(segments[2])) return null;
  return { path: segments[0], entityId: segments[1] };
};

/**
 * The subcollections whose gate needs the parent onboarding document — both of them, and both for
 * the same reason: their status fields sit at the document ROOT and their preconditions live on the
 * parent's env-scoped buckets.
 */
const ONBOARDING_PARENT_FETCHING_SUBCOLLECTIONS: readonly string[] = [
  ONBOARDING_SHOWCASE_REQUESTS_PATH,
  ONBOARDING_GALLERY_PHOTOS_PATH,
];
